import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Languages, Mic, Settings, Globe, Home } from "lucide-react";

// Dashboard Components
import {
  LANGUAGE_OPTIONS,
  getLanguageName,
  UserInfoBar,
  AudioInputCard,
  StatusCard,
  SecondWindowCard,
  OBSIntegrationCard,
  TranslationPreviewCard,
  DeviceSettingsCard,
  DelaySettingsCard,
  GateSettingsCard,
  ReadingSpeedCard,
  HeaderSettingsCard,
  FooterSettingsCard,
  OverlaySettingsCard,
  TranslationHistoryCard
} from "@/components/dashboard";
import { TranslationRoomCard } from "@/components/dashboard/TranslationRoomCard";
import { PermanentRoomCard } from "@/components/dashboard/PermanentRoomCard";
import { SystemTestCard } from "@/components/dashboard/SystemTestCard";
import { useTranslationPresenter } from "@/hooks/useTranslationPresenter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { t, language, setLanguage } = useLanguage();
  const { user, logout, updateUsage, isPro, isGuest, remainingSeconds } = useAuth();
  const navigate = useNavigate();
  useTranslationPresenter();

  // Persisted preferences (survive page refresh so an event never loses its setup)
  const savedPrefs = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("dashboardPrefs") || "{}"); } catch { return {}; }
  }, []);
  const savedWindowSettings = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("translationWindowSettings") || "{}"); } catch { return {}; }
  }, []);
  const savedHeader = savedWindowSettings.header || {};
  const savedFooter = savedWindowSettings.footer || {};
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("idle");
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(savedPrefs.selectedDevice || "default");
  const [delayMs, setDelayMs] = useState(savedPrefs.delayMs ?? 500);
  const [readingSpeed, setReadingSpeed] = useState(() => {
    return parseInt(localStorage.getItem("translationWindow_readingSpeed") || "15", 10);
  });
  const [currentTranslation, setCurrentTranslation] = useState("");
  const [originalText, setOriginalText] = useState("");
  const [detectedSourceLang, setDetectedSourceLang] = useState(""); // Actual language detected by Whisper
  const [translationHistory, setTranslationHistory] = useState([]);
  const [gateThreshold, setGateThreshold] = useState(savedPrefs.gateThreshold ?? -30);
  const [currentAudioLevel, setCurrentAudioLevel] = useState(-60);
  const [isGateOpen, setIsGateOpen] = useState(false);
  
  // Audio source type
  const [audioSourceType, setAudioSourceType] = useState(savedPrefs.audioSourceType || "microphone");
  const systemStreamRef = useRef(null);
  
  // Usage tracking
  const usageIntervalRef = useRef(null);
  const [localRemainingSeconds, setLocalRemainingSeconds] = useState(remainingSeconds);
  
  // Second Translation Window states
  const [secondWindowEnabled, setSecondWindowEnabled] = useState(savedPrefs.secondWindowEnabled ?? false);
  const [secondTargetLang, setSecondTargetLang] = useState(savedPrefs.secondTargetLang || "");
  
  // OBS Integration settings
  const [obsSettings, setObsSettings] = useState({
    bg: "transparent",
    fontSize: 48,
    color: "#ffffff",
    position: "bottom",
    shadow: true,
    windowType: "main"
  });
  const [generatedObsUrl, setGeneratedObsUrl] = useState("");
  
  // Header/Footer box settings (restored from translationWindowSettings)
  const [headerEnabled, setHeaderEnabled] = useState(savedHeader.enabled ?? false);
  const [headerText, setHeaderText] = useState(savedHeader.text ?? "Digite Aqui Sua Mensagem");
  const [headerFontSize, setHeaderFontSize] = useState(savedHeader.fontSize ?? 24);
  const [headerTextColor, setHeaderTextColor] = useState(savedHeader.textColor ?? "#000000");
  const [headerBgColor, setHeaderBgColor] = useState(savedHeader.bgColor ?? "#FFFFFF");
  const [headerBorderRadius, setHeaderBorderRadius] = useState(savedHeader.borderRadius ?? 8);
  const [headerPadding, setHeaderPadding] = useState(savedHeader.padding ?? 16);
  
  const [footerEnabled, setFooterEnabled] = useState(savedFooter.enabled ?? false);
  const [footerText, setFooterText] = useState(savedFooter.text ?? "Esta tradução é feita de forma automática. Pedimos desculpas por possíveis erros que possam acontecer. / This translation is done automatically. We apologize for any errors that may occur.");
  const [footerFontSize, setFooterFontSize] = useState(savedFooter.fontSize ?? 16);
  const [footerTextColor, setFooterTextColor] = useState(savedFooter.textColor ?? "#000000");
  const [footerBgColor, setFooterBgColor] = useState(savedFooter.bgColor ?? "#FFFFFF");
  const [footerBorderRadius, setFooterBorderRadius] = useState(savedFooter.borderRadius ?? 8);
  const [footerPadding, setFooterPadding] = useState(savedFooter.padding ?? 12);
  
  // Multi-screen detection
  const [hasSecondScreen, setHasSecondScreen] = useState(false);
  const [secondScreenInfo, setSecondScreenInfo] = useState(null);
  
  // Overlay mode
  const [overlayMode, setOverlayMode] = useState(savedWindowSettings.overlayMode ?? false);
  
  // Selected translation languages
  const [translationSourceLang, setTranslationSourceLang] = useState("");
  const [translationTargetLang, setTranslationTargetLang] = useState("");
  
  // Active room presenter languages (for improved Whisper detection)
  const [activePresenterLanguages, setActivePresenterLanguages] = useState([]);
  
  // Refs for recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const isRecordingRef = useRef(false);
  const delayMsRef = useRef(delayMs);
  const gateThresholdRef = useRef(gateThreshold);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioLevelIntervalRef = useRef(null);
  const hasAudioAboveThresholdRef = useRef(false);
  const sendAudioRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const firstTranslationWindowRef = useRef(null);

  // Sync refs with state
  useEffect(() => { delayMsRef.current = delayMs; }, [delayMs]);
  useEffect(() => { gateThresholdRef.current = gateThreshold; }, [gateThreshold]);
  useEffect(() => { setLocalRemainingSeconds(remainingSeconds); }, [remainingSeconds]);

  // Persist dashboard preferences
  useEffect(() => {
    localStorage.setItem("dashboardPrefs", JSON.stringify({
      selectedDevice, delayMs, gateThreshold, audioSourceType, secondWindowEnabled, secondTargetLang
    }));
  }, [selectedDevice, delayMs, gateThreshold, audioSourceType, secondWindowEnabled, secondTargetLang]);

  // Sync header settings to localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem("translationWindowSettings");
    const currentSettings = savedSettings ? JSON.parse(savedSettings) : {};
    currentSettings.header = { 
      enabled: headerEnabled, text: headerText, fontSize: headerFontSize, 
      textColor: headerTextColor, bgColor: headerBgColor, 
      borderRadius: headerBorderRadius, padding: headerPadding 
    };
    localStorage.setItem("translationWindowSettings", JSON.stringify(currentSettings));
  }, [headerEnabled, headerText, headerFontSize, headerTextColor, headerBgColor, headerBorderRadius, headerPadding]);

  // Sync footer settings to localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem("translationWindowSettings");
    const currentSettings = savedSettings ? JSON.parse(savedSettings) : {};
    currentSettings.footer = { 
      enabled: footerEnabled, text: footerText, fontSize: footerFontSize, 
      textColor: footerTextColor, bgColor: footerBgColor, 
      borderRadius: footerBorderRadius, padding: footerPadding 
    };
    localStorage.setItem("translationWindowSettings", JSON.stringify(currentSettings));
  }, [footerEnabled, footerText, footerFontSize, footerTextColor, footerBgColor, footerBorderRadius, footerPadding]);

  // Sync overlay mode to localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem("translationWindowSettings");
    const currentSettings = savedSettings ? JSON.parse(savedSettings) : {};
    currentSettings.overlayMode = overlayMode;
    localStorage.setItem("translationWindowSettings", JSON.stringify(currentSettings));
  }, [overlayMode]);

  // Persist reading speed (consumed by TranslationWindow / TranslationWindow2 queue hook)
  useEffect(() => {
    localStorage.setItem("translationWindow_readingSpeed", readingSpeed.toString());
  }, [readingSpeed]);

  // Load translation languages from localStorage
  useEffect(() => {
    const sourceLang = localStorage.getItem("translation_source_lang") || "pt";
    const targetLang = localStorage.getItem("translation_target_lang") || "en";
    setTranslationSourceLang(sourceLang);
    setTranslationTargetLang(targetLang);
  }, []);

  // Track usage while recording (only for Free/Guest plan)
  useEffect(() => {
    if (isRecording && !isPro) {
      usageIntervalRef.current = setInterval(() => {
        setLocalRemainingSeconds(prev => {
          if (prev <= 0) {
            stopRecording();
            toast.error("Tempo esgotado! Faça upgrade para o Plano PRO para tradução ilimitada.");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (usageIntervalRef.current) clearInterval(usageIntervalRef.current);
    }
    return () => { if (usageIntervalRef.current) clearInterval(usageIntervalRef.current); };
  }, [isRecording, isPro]);

  // Detect multiple screens
  useEffect(() => {
    const detectScreens = async () => {
      try {
        if ('getScreenDetails' in window) {
          try {
            const screenDetails = await window.getScreenDetails();
            const allScreens = screenDetails.screens;
            if (allScreens.length > 1) {
              setHasSecondScreen(true);
              const currentScreen = screenDetails.currentScreen;
              const secondScreen = allScreens.find(s => s !== currentScreen) || allScreens[1];
              setSecondScreenInfo({
                left: secondScreen.left, top: secondScreen.top,
                width: secondScreen.width, height: secondScreen.height,
                label: secondScreen.label || 'Monitor 2'
              });
            } else {
              setHasSecondScreen(false);
              setSecondScreenInfo(null);
            }
            screenDetails.addEventListener('screenschange', detectScreens);
          } catch {
            fallbackScreenDetection();
          }
        } else {
          fallbackScreenDetection();
        }
      } catch {
        fallbackScreenDetection();
      }
    };

    const fallbackScreenDetection = () => {
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const availLeft = window.screen.availLeft || 0;
      if (availLeft !== 0 || (screenWidth > 0 && window.screenX !== undefined)) {
        setHasSecondScreen(true);
        setSecondScreenInfo({ left: screenWidth, top: 0, width: screenWidth, height: screenHeight, label: 'Monitor 2' });
      } else {
        const savedSecondScreen = localStorage.getItem('secondScreenPreference');
        if (savedSecondScreen) {
          setHasSecondScreen(true);
          setSecondScreenInfo(JSON.parse(savedSecondScreen));
        } else {
          setHasSecondScreen(false);
          setSecondScreenInfo(null);
        }
      }
    };

    detectScreens();
    window.addEventListener('resize', detectScreens);
    return () => window.removeEventListener('resize', detectScreens);
  }, []);

  // Load audio devices
  useEffect(() => {
    const loadDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(device => device.kind === 'audioinput');
        setAudioDevices(audioInputs);
        if (audioInputs.length > 0 && !selectedDevice) {
          setSelectedDevice(audioInputs[0].deviceId);
        }
      } catch (error) {
        console.error("Error loading audio devices:", error);
        toast.error("Erro ao carregar dispositivos de áudio");
      }
    };
    loadDevices();
  }, []);

  // Utility functions
  const formatTime = (seconds) => {
    if (seconds < 0) return "Ilimitado";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  const syncUsageToServer = async (seconds) => {
    if (seconds > 0 && !isPro) {
      await updateUsage(seconds);
    }
  };

  // Retry function with exponential backoff
  const retryWithBackoff = useCallback(async (fn, maxRetries = 3, initialDelay = 1000) => {
    let delay = initialDelay;
    let lastError = null;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        const errorStr = String(error).toLowerCase();
        if (error.status === 429 || errorStr.includes('429') || errorStr.includes('rate') || errorStr.includes('limit')) {
          if (attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
            delay *= 2;
            continue;
          }
        }
        throw error;
      }
    }
    throw lastError;
  }, []);

  // Send audio for transcription
  const sendAudioForTranscription = useCallback(async (audioBlob) => {
    console.log("[TRANSLATION] Sending audio blob, size:", audioBlob.size);
    const formData = new FormData();
    formData.append("file", audioBlob, "audio.webm");

    const sourceLang = localStorage.getItem("translation_source_lang") || "pt";
    const targetLang = localStorage.getItem("translation_target_lang") || "en";
    const secondLang = secondWindowEnabled ? secondTargetLang : null;
    const uiLang = language || localStorage.getItem("selected_language") || "pt";

    const doFetch = async () => {
      let url = `${BACKEND_URL}/api/transcribe-and-translate?source_lang=${sourceLang}&target_lang=${targetLang}`;
      if (secondLang) url += `&target_lang_2=${secondLang}&ui_lang=${uiLang}`;
      
      // Add presenter languages hint for improved Whisper detection
      if (activePresenterLanguages && activePresenterLanguages.length > 0) {
        url += `&presenter_languages=${activePresenterLanguages.join(",")}`;
        console.log("[TRANSLATION] Using presenter languages hint:", activePresenterLanguages);
      }
      
      // Note: Do not use credentials: "include" as it can cause CORS issues on some browsers
      const response = await fetch(url, { method: "POST", body: formData, mode: "cors" });
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}`);
        error.status = response.status;
        if (response.status === 429 || response.status >= 500) throw error;
        return null;
      }
      return response.json();
    };

    try {
      const data = await retryWithBackoff(doFetch, 3, 1000);
      console.log("[TRANSLATION] API Response:", data);
      if (!data) {
        console.log("[TRANSLATION] No data returned from API");
        return;
      }
      if (data.error) {
        console.log("[TRANSLATION] API returned error:", data.error);
        return;
      }
      
      if (data.original && data.original.trim()) {
        setOriginalText(data.original);
        const translation = data.translation || "...";
        const respSourceLang = data.source_lang || "unknown";
        const respTargetLang = data.target_lang || "unknown";
        const respDetectedLang = data.detected_lang || respSourceLang; // Use detected language from Whisper
        
        setCurrentTranslation(translation);
        setDetectedSourceLang(respDetectedLang); // Save the actual detected language
        
        console.log("[TRANSLATION] Saving to localStorage:", translation, "Detected lang:", respDetectedLang);
        localStorage.setItem("currentTranslation", translation);
        localStorage.setItem("translationTimestamp", Date.now().toString());
        localStorage.setItem("sourceLang", respSourceLang);
        localStorage.setItem("targetLang", respTargetLang);
        localStorage.setItem("detectedLang", respDetectedLang);
        
        if (secondLang && data.translation_2) {
          localStorage.setItem("currentTranslation2", data.translation_2);
          localStorage.setItem("translationTimestamp2", Date.now().toString());
          localStorage.setItem("sourceLang2", respSourceLang);
          localStorage.setItem("targetLang2", data.target_lang_2 || secondLang);
        }
        
        setTranslationHistory(prev => [
          { original: data.original, translated: translation, translated2: data.translation_2 || null,
            sourceLang: respSourceLang, targetLang: respTargetLang, targetLang2: data.target_lang_2 || null,
            timestamp: new Date().toISOString() },
          ...prev.slice(0, 49)
        ]);
      }
    } catch (error) {
      console.error("Transcription/Translation error after retries:", error);
      if (error.status === 429) {
        toast.error("Limite de requisições excedido. Aguarde alguns segundos e tente novamente.");
      }
    }
  }, [retryWithBackoff, secondWindowEnabled, secondTargetLang, language, activePresenterLanguages]);

  useEffect(() => { sendAudioRef.current = sendAudioForTranscription; }, [sendAudioForTranscription]);

  // Recording functions
  const startRecording = async () => {
    if (!isPro && localRemainingSeconds <= 0) {
      toast.error("Tempo esgotado! Faça upgrade para o Plano PRO para tradução ilimitada.");
      navigate("/pricing");
      return;
    }

    try {
      setConnectionStatus("connecting");
      let combinedStream;
      
      // Detect iOS/Safari for simplified constraints
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const needsSimpleConstraints = isIOS || isSafari;
      
      console.log("[RECORDING] Device detection - iOS:", isIOS, "Safari:", isSafari, "Simple constraints:", needsSimpleConstraints);
      
      if (audioSourceType === "microphone") {
        // iOS Safari doesn't support some audio constraints, use simplified version
        let audioConstraints;
        if (needsSimpleConstraints) {
          // Simple constraints for iOS/Safari - just request audio
          audioConstraints = true;
          console.log("[RECORDING] Using simple audio constraints for iOS/Safari");
        } else {
          // Full constraints for other browsers
          audioConstraints = { 
            deviceId: selectedDevice && selectedDevice !== "default" ? { ideal: selectedDevice } : undefined, 
            echoCancellation: { ideal: true }, 
            noiseSuppression: { ideal: true } 
          };
        }
        
        combinedStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      } else if (audioSourceType === "system") {
        // iOS doesn't support getDisplayMedia for system audio
        if (needsSimpleConstraints) {
          console.log("[RECORDING] iOS detected - system audio not supported, using microphone");
          toast.warning("Captura de áudio do sistema não é suportada no iOS. Usando microfone.");
          combinedStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
          const displayStream = await navigator.mediaDevices.getDisplayMedia({
            video: true, audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
          });
          systemStreamRef.current = displayStream;
          const audioTracks = displayStream.getAudioTracks();
          if (audioTracks.length === 0) {
            displayStream.getTracks().forEach(track => track.stop());
            throw new Error("Áudio do sistema não disponível. Marque 'Compartilhar áudio' ao selecionar a tela.");
          }
          combinedStream = new MediaStream(audioTracks);
          displayStream.getVideoTracks().forEach(track => track.stop());
        }
      } else if (audioSourceType === "both") {
        // iOS doesn't support system audio capture, so just use microphone
        if (needsSimpleConstraints) {
          console.log("[RECORDING] iOS detected - 'both' mode will use microphone only");
          combinedStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          toast.info("No iOS, apenas o microfone está disponível.");
        } else {
          // Desktop: try to capture both mic and system audio
          const micConstraints = { 
            deviceId: selectedDevice && selectedDevice !== "default" ? { ideal: selectedDevice } : undefined, 
            echoCancellation: { ideal: true }, 
            noiseSuppression: { ideal: true } 
          };
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints });
          try {
            const displayStream = await navigator.mediaDevices.getDisplayMedia({
              video: true, audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
            });
            systemStreamRef.current = displayStream;
            const audioTracks = displayStream.getAudioTracks();
            if (audioTracks.length === 0) {
              combinedStream = micStream;
            } else {
              const systemStream = new MediaStream(audioTracks);
              displayStream.getVideoTracks().forEach(track => track.stop());
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const destination = audioContext.createMediaStreamDestination();
              const micSource = audioContext.createMediaStreamSource(micStream);
              const systemSource = audioContext.createMediaStreamSource(systemStream);
              micSource.connect(audioContext.createGain()).connect(destination);
              systemSource.connect(audioContext.createGain()).connect(destination);
              combinedStream = destination.stream;
            }
          } catch {
            toast.warning("Não foi possível capturar áudio do sistema. Usando apenas o microfone.");
            combinedStream = micStream;
          }
        }
      }
      
      streamRef.current = combinedStream;
      
      // Set up audio level monitoring
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(combinedStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      analyserRef.current = analyser;
      
      const dataArray = new Float32Array(analyser.fftSize);
      audioLevelIntervalRef.current = setInterval(() => {
        if (analyserRef.current) {
          analyserRef.current.getFloatTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
          const rms = Math.sqrt(sum / dataArray.length);
          const db = rms > 0 ? 20 * Math.log10(rms) : -60;
          const clampedDb = Math.max(-60, Math.min(0, db));
          setCurrentAudioLevel(clampedDb);
          const gateOpen = clampedDb >= gateThresholdRef.current;
          setIsGateOpen(gateOpen);
          if (gateOpen) hasAudioAboveThresholdRef.current = true;
        }
      }, 50);
      
      recordingStartTimeRef.current = Date.now();
      isRecordingRef.current = true;
      setIsRecording(true);
      setConnectionStatus("connected");
      localStorage.setItem("dashboardRecordingActive", audioSourceType);
      
      const sourceLabel = audioSourceType === "microphone" ? "Microfone" : 
                          audioSourceType === "system" ? "Áudio do Sistema" : "Microfone + Sistema";
      toast.success(`Gravação iniciada (${sourceLabel}, Gate: ${gateThreshold}dB)`);
      startRecordingChunk();
    } catch (error) {
      console.error("Error starting recording:", error);
      setConnectionStatus("error");
      
      // Provide user-friendly error messages
      let errorMessage = error.message || "Erro desconhecido";
      
      // Handle specific iOS/Safari errors
      if (errorMessage.includes("Invalid constraint") || errorMessage.includes("OverconstrainedError")) {
        errorMessage = "Configuração de áudio não suportada. Tente usar apenas o microfone.";
      } else if (errorMessage.includes("NotAllowedError") || errorMessage.includes("Permission denied")) {
        errorMessage = "Permissão de microfone negada. Por favor, permita o acesso nas configurações.";
      } else if (errorMessage.includes("NotFoundError") || errorMessage.includes("No device")) {
        errorMessage = "Nenhum microfone encontrado. Verifique se o dispositivo está conectado.";
      } else if (errorMessage.includes("NotSupportedError")) {
        errorMessage = "Este navegador não suporta gravação de áudio.";
      }
      
      toast.error("Erro ao iniciar gravação: " + errorMessage);
    }
  };

  // Store detected mime type for blob creation
  const detectedMimeTypeRef = useRef('audio/webm');
  
  const startRecordingChunk = () => {
    if (!streamRef.current || !isRecordingRef.current) return;
    hasAudioAboveThresholdRef.current = false;
    try {
      // Determine the best supported audio format
      // iOS Safari supports mp4/aac but not webm
      let mimeType = 'audio/webm;codecs=opus';
      let blobType = 'audio/webm';
      
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Try alternatives for iOS Safari
        const alternatives = [
          { mime: 'audio/mp4', blob: 'audio/mp4' },
          { mime: 'audio/aac', blob: 'audio/aac' },
          { mime: 'audio/webm', blob: 'audio/webm' },
          { mime: 'audio/ogg', blob: 'audio/ogg' },
          { mime: '', blob: 'audio/webm' } // Default - let browser decide
        ];
        
        for (const alt of alternatives) {
          if (alt.mime === '' || MediaRecorder.isTypeSupported(alt.mime)) {
            mimeType = alt.mime || undefined;
            blobType = alt.blob;
            console.log("[RECORDING] Using audio format:", mimeType || 'browser default');
            break;
          }
        }
      }
      
      detectedMimeTypeRef.current = blobType;
      const recorderOptions = mimeType ? { mimeType } : undefined;
      const mediaRecorder = new MediaRecorder(streamRef.current, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const chunks = [...audioChunksRef.current];
        const hadAudioAboveThreshold = hasAudioAboveThresholdRef.current;
        audioChunksRef.current = [];
        
        console.log("[RECORDING] Chunk stopped - chunks:", chunks.length, "hadAudioAboveThreshold:", hadAudioAboveThreshold);
        
        if (chunks.length > 0 && hadAudioAboveThreshold) {
          // Use the actual mime type from the recorder if available
          const actualMimeType = mediaRecorder.mimeType || detectedMimeTypeRef.current;
          const audioBlob = new Blob(chunks, { type: actualMimeType });
          console.log("[RECORDING] Audio blob created, size:", audioBlob.size, "type:", actualMimeType);
          if (audioBlob.size > 1000 && sendAudioRef.current) {
            console.log("[RECORDING] Sending audio to transcription...");
            sendAudioRef.current(audioBlob);
          } else {
            console.log("[RECORDING] Audio blob too small or sendAudioRef not available");
          }
        } else {
          console.log("[RECORDING] Skipping - no chunks or audio below threshold");
        }
        if (isRecordingRef.current && streamRef.current) startRecordingChunk();
      };

      mediaRecorder.start();
      const chunkDuration = 5000 + delayMsRef.current;
      setTimeout(() => {
        if (mediaRecorder.state === "recording" && isRecordingRef.current) mediaRecorder.stop();
      }, chunkDuration);
    } catch (error) {
      console.error("Error in recording chunk:", error);
      if (isRecordingRef.current && streamRef.current) setTimeout(startRecordingChunk, 1000);
    }
  };

  const stopRecording = () => {
    isRecordingRef.current = false;
    localStorage.removeItem("dashboardRecordingActive");
    if (recordingStartTimeRef.current && !isPro) {
      const elapsedSeconds = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
      syncUsageToServer(elapsedSeconds);
      recordingStartTimeRef.current = null;
    }
    if (audioLevelIntervalRef.current) { clearInterval(audioLevelIntervalRef.current); audioLevelIntervalRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; analyserRef.current = null; }
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    if (streamRef.current) { streamRef.current.getTracks().forEach(track => track.stop()); streamRef.current = null; }
    if (systemStreamRef.current) { systemStreamRef.current.getTracks().forEach(track => track.stop()); systemStreamRef.current = null; }
    setIsRecording(false);
    setConnectionStatus("idle");
    setCurrentAudioLevel(-60);
    setIsGateOpen(false);
    toast.info("Gravação encerrada");
  };

  // Auto-resume recording after a page refresh (mic only — system audio needs a user click)
  const startRecordingRef = useRef(null);
  useEffect(() => { startRecordingRef.current = startRecording; });
  const autoResumeDoneRef = useRef(false);
  useEffect(() => {
    if (autoResumeDoneRef.current || audioDevices.length === 0) return;
    const previous = localStorage.getItem("dashboardRecordingActive");
    if (!previous) return;
    autoResumeDoneRef.current = true;
    if (previous === "microphone") {
      toast.info(t("recordingAutoResumed"));
      startRecordingRef.current?.();
    } else {
      localStorage.removeItem("dashboardRecordingActive");
      toast.warning(t("recordingResumeNeedsClick"), { duration: 10000 });
    }
  }, [audioDevices, t]);

  const openTranslationWindow = () => {
    const params = new URLSearchParams({ text: currentTranslation || "Aguardando tradução..." });
    const win = window.open(`/translation?${params.toString()}`, 'translation_window_1', 'width=1200,height=800');
    if (win) firstTranslationWindowRef.current = win;
  };

  const positionWindowWithRetries = (win, left, top, width, height) => {
    if (!win) return;
    [200, 700, 1500, 2500].forEach(delay => {
      setTimeout(() => {
        try {
          if (win.closed) return;
          win.moveTo(left, top);
          win.resizeTo(width, height);
        } catch {}
      }, delay);
    });
  };

  const openSplitScreenWindows = async () => {
    let screenBounds = null;
    let hasWindowManagementPermission = false;
    try {
      if ('getScreenDetails' in window) {
        const screenDetails = await window.getScreenDetails();
        hasWindowManagementPermission = true;
        const allScreens = screenDetails.screens;
        if (allScreens.length > 1) {
          const currentScreen = screenDetails.currentScreen;
          const target = allScreens.find(s => s !== currentScreen) || allScreens.find(s => !s.isPrimary) || allScreens[1];
          screenBounds = {
            left: target.availLeft !== undefined ? target.availLeft : target.left,
            top: target.availTop !== undefined ? target.availTop : target.top,
            width: target.availWidth || target.width,
            height: target.availHeight || target.height,
            label: target.label || 'Monitor 2'
          };
        }
      }
    } catch (err) {
      if (err && err.name === 'NotAllowedError') {
        toast.warning(t("splitScreenPermission"), { duration: 8000 });
      }
    }
    if (!screenBounds && secondScreenInfo) screenBounds = secondScreenInfo;
    if (!screenBounds) {
      toast.warning(t("splitScreenNoScreen"));
      return;
    }
    const { left, top, width, height } = screenBounds;
    const halfWidth = Math.floor(width / 2);
    const rightWidth = width - halfWidth;
    const features = (l, w) => `popup=yes,left=${l},top=${top},width=${w},height=${height}`;
    const selfPosParams = (l, w) => `sx=${l}&sy=${top}&sw=${w}&sh=${height}`;
    const looksFarbled = hasWindowManagementPermission && (width < 640 || height < 480);
    if (looksFarbled) {
      toast.warning(t("splitScreenBraveShields"), { duration: 12000 });
    }

    let firstWin = firstTranslationWindowRef.current;
    if (firstWin && !firstWin.closed) {
      try { firstWin.focus(); } catch {}
    } else {
      const params = new URLSearchParams({ text: currentTranslation || "Aguardando tradução..." });
      firstWin = window.open(`/translation?${params.toString()}&${selfPosParams(left, halfWidth)}`, 'translation_window_1', features(left, halfWidth));
      if (firstWin) firstTranslationWindowRef.current = firstWin;
    }
    positionWindowWithRetries(firstWin, left, top, halfWidth, height);

    const secondWin = window.open(`${window.location.origin}/translation2?${selfPosParams(left + halfWidth, rightWidth)}`, 'translation_window_2', features(left + halfWidth, rightWidth));
    positionWindowWithRetries(secondWin, left + halfWidth, top, rightWidth, height);

    if (!firstWin || !secondWin) {
      toast.error(t("splitScreenPopupBlocked"), { duration: 8000 });
      return;
    }
    if (hasWindowManagementPermission) {
      if (!looksFarbled) toast.success(t("splitScreenSuccess"));
    } else if (!('getScreenDetails' in window)) {
      toast.warning(t("splitScreenPermission"), { duration: 8000 });
    }
  };

  const openTranslationOnSecondScreen = async () => {
    const params = new URLSearchParams({ text: currentTranslation || "Aguardando tradução..." });
    const url = `/translation?${params.toString()}`;
    try {
      if ('getScreenDetails' in window && secondScreenInfo) {
        const screenDetails = await window.getScreenDetails();
        const allScreens = screenDetails.screens;
        if (allScreens.length > 1) {
          const currentScreen = screenDetails.currentScreen;
          const targetScreen = allScreens.find(s => s !== currentScreen) || allScreens[1];
          const tLeft = targetScreen.availLeft !== undefined ? targetScreen.availLeft : targetScreen.left;
          const tTop = targetScreen.availTop !== undefined ? targetScreen.availTop : targetScreen.top;
          const tWidth = targetScreen.availWidth || targetScreen.width;
          const tHeight = targetScreen.availHeight || targetScreen.height;
          const newWindow = window.open(`${url}&sx=${tLeft}&sy=${tTop}&sw=${tWidth}&sh=${tHeight}`, 'translation_window_1', `popup=yes,left=${tLeft},top=${tTop},width=${tWidth},height=${tHeight}`);
          if (newWindow) {
            firstTranslationWindowRef.current = newWindow;
            setTimeout(() => {
              try { newWindow.moveTo(tLeft, tTop); newWindow.resizeTo(tWidth, tHeight); } catch {}
            }, 500);
            toast.success(`Janela aberta na segunda tela: ${targetScreen.label || 'Monitor 2'}`);
          }
          return;
        }
      }
      if (secondScreenInfo) {
        const newWindow = window.open(url, 'translation_window_1', `left=${secondScreenInfo.left},top=${secondScreenInfo.top},width=${secondScreenInfo.width},height=${secondScreenInfo.height}`);
        if (newWindow) {
          firstTranslationWindowRef.current = newWindow;
          localStorage.setItem('secondScreenPreference', JSON.stringify(secondScreenInfo));
          toast.success("Janela aberta na segunda tela");
        }
      } else {
        toast.warning("Segunda tela não detectada. Abrindo na tela principal.");
        openTranslationWindow();
      }
    } catch {
      toast.error("Erro ao abrir na segunda tela. Abrindo na tela principal.");
      openTranslationWindow();
    }
  };

  const requestScreenPermission = async () => {
    try {
      if ('getScreenDetails' in window) {
        const screenDetails = await window.getScreenDetails();
        const allScreens = screenDetails.screens;
        if (allScreens.length > 1) {
          setHasSecondScreen(true);
          const currentScreen = screenDetails.currentScreen;
          const secondScreen = allScreens.find(s => s !== currentScreen) || allScreens[1];
          setSecondScreenInfo({ left: secondScreen.left, top: secondScreen.top, width: secondScreen.width, height: secondScreen.height, label: secondScreen.label || 'Monitor 2' });
          toast.success(`${allScreens.length} screens detected!`);
        } else {
          setHasSecondScreen(false);
          toast.info("Only one screen detected.");
        }
      } else {
        toast.warning("Your browser does not support advanced screen detection.");
      }
    } catch (error) {
      if (error.name === 'NotAllowedError') toast.error("Permission denied.");
      else toast.error("Erro ao detectar telas.");
    }
  };

  const goToHome = () => {
    localStorage.removeItem("app_ui_language");
    localStorage.removeItem("translation_source_lang");
    localStorage.removeItem("translation_target_lang");
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* User Info Bar */}
        <UserInfoBar
          user={user}
          isPro={isPro}
          isGuest={isGuest}
          localRemainingSeconds={localRemainingSeconds}
          formatTime={formatTime}
          handleLogout={handleLogout}
        />

        {/* Home Button */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <Button onClick={goToHome} variant="outline" className="border-zinc-700 hover:bg-zinc-800 text-white gap-2 text-sm sm:text-base" data-testid="home-button">
            <Home className="w-4 h-4" />
            {t("homeButton")}
          </Button>
        </div>

        {/* Header */}
        <header className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <Languages className="w-6 h-6 sm:w-8 sm:h-8 text-white flex-shrink-0" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white font-['Outfit']" data-testid="app-title">
                {t("appTitle")}
              </h1>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-700 text-white" data-testid="language-selector">
                <Globe className="w-4 h-4 mr-2 flex-shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                {LANGUAGE_OPTIONS.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code} className="text-white hover:bg-zinc-800">
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-zinc-400 text-sm sm:text-base">
            {translationSourceLang && translationTargetLang 
              ? `${getLanguageName(translationSourceLang)} ↔ ${getLanguageName(translationTargetLang)}`
              : t("appSubtitle")
            }
          </p>
        </header>

        {/* Main Content */}
        <Tabs defaultValue="control" className="space-y-4 sm:space-y-6">
          <TabsList className="glass p-1 rounded-xl w-full sm:w-auto" data-testid="main-tabs">
            <TabsTrigger value="control" className="flex-1 sm:flex-initial data-[state=active]:bg-white data-[state=active]:text-black rounded-lg px-3 sm:px-6 text-sm sm:text-base" data-testid="control-tab">
              <Mic className="w-4 h-4 mr-1 sm:mr-2" />
              {t("controlTab")}
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex-1 sm:flex-initial data-[state=active]:bg-white data-[state=active]:text-black rounded-lg px-3 sm:px-6 text-sm sm:text-base" data-testid="settings-tab">
              <Settings className="w-4 h-4 mr-1 sm:mr-2" />
              {t("settingsTab")}
            </TabsTrigger>
          </TabsList>

          {/* Control Tab */}
          {/* forceMount: keep both tabs mounted so room cards (WebSocket, heartbeat,
              active room state) survive switching to Settings — only hide inactive */}
          <TabsContent value="control" forceMount className="space-y-6 data-[state=inactive]:hidden">
            <div className="bento-grid">
              <AudioInputCard
                audioSourceType={audioSourceType}
                setAudioSourceType={setAudioSourceType}
                isRecording={isRecording}
                startRecording={startRecording}
                stopRecording={stopRecording}
                currentAudioLevel={currentAudioLevel}
                gateThreshold={gateThreshold}
                isGateOpen={isGateOpen}
              />

              <StatusCard
                connectionStatus={connectionStatus}
                isRecording={isRecording}
                openTranslationWindow={openTranslationWindow}
                openTranslationOnSecondScreen={openTranslationOnSecondScreen}
                hasSecondScreen={hasSecondScreen}
                secondScreenInfo={secondScreenInfo}
                requestScreenPermission={requestScreenPermission}
              />

              <SecondWindowCard
                isPro={isPro}
                secondWindowEnabled={secondWindowEnabled}
                setSecondWindowEnabled={setSecondWindowEnabled}
                secondTargetLang={secondTargetLang}
                setSecondTargetLang={setSecondTargetLang}
                translationSourceLang={translationSourceLang}
                translationTargetLang={translationTargetLang}
                language={language}
                hasSecondScreen={hasSecondScreen}
                onOpenSplitScreen={openSplitScreenWindows}
              />

              <PermanentRoomCard
                isPro={isPro}
                userId={user?.id}
                sourceLang={translationSourceLang}
                targetLang={translationTargetLang}
                currentTranslation={currentTranslation}
                originalText={originalText}
                detectedSourceLang={detectedSourceLang}
                onPresenterLanguagesChange={setActivePresenterLanguages}
              />

              <OBSIntegrationCard
                isPro={isPro}
                obsSettings={obsSettings}
                setObsSettings={setObsSettings}
                generatedObsUrl={generatedObsUrl}
                setGeneratedObsUrl={setGeneratedObsUrl}
                secondWindowEnabled={secondWindowEnabled}
                secondTargetLang={secondTargetLang}
                translationTargetLang={translationTargetLang}
              />

              <TranslationRoomCard
                isPro={isPro}
                sourceLang={translationSourceLang}
                targetLang={translationTargetLang}
                currentTranslation={currentTranslation}
                originalText={originalText}
                detectedSourceLang={detectedSourceLang}
                isRecording={isRecording}
                onPresenterLanguagesChange={setActivePresenterLanguages}
              />

              <TranslationPreviewCard
                translationSourceLang={translationSourceLang}
                translationTargetLang={translationTargetLang}
                originalText={originalText}
                currentTranslation={currentTranslation}
              />
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" forceMount className="space-y-6 data-[state=inactive]:hidden">
            <div className="bento-grid">
              <DeviceSettingsCard audioDevices={audioDevices} selectedDevice={selectedDevice} setSelectedDevice={setSelectedDevice} />
              <DelaySettingsCard delayMs={delayMs} setDelayMs={setDelayMs} />
              <ReadingSpeedCard readingSpeed={readingSpeed} setReadingSpeed={setReadingSpeed} />
              <GateSettingsCard gateThreshold={gateThreshold} setGateThreshold={setGateThreshold} />
              <SystemTestCard />
              
              <HeaderSettingsCard
                headerEnabled={headerEnabled} setHeaderEnabled={setHeaderEnabled}
                headerText={headerText} setHeaderText={setHeaderText}
                headerFontSize={headerFontSize} setHeaderFontSize={setHeaderFontSize}
                headerTextColor={headerTextColor} setHeaderTextColor={setHeaderTextColor}
                headerBgColor={headerBgColor} setHeaderBgColor={setHeaderBgColor}
                headerBorderRadius={headerBorderRadius} setHeaderBorderRadius={setHeaderBorderRadius}
                headerPadding={headerPadding} setHeaderPadding={setHeaderPadding}
              />
              
              <FooterSettingsCard
                footerEnabled={footerEnabled} setFooterEnabled={setFooterEnabled}
                footerText={footerText} setFooterText={setFooterText}
                footerFontSize={footerFontSize} setFooterFontSize={setFooterFontSize}
                footerTextColor={footerTextColor} setFooterTextColor={setFooterTextColor}
                footerBgColor={footerBgColor} setFooterBgColor={setFooterBgColor}
                footerBorderRadius={footerBorderRadius} setFooterBorderRadius={setFooterBorderRadius}
                footerPadding={footerPadding} setFooterPadding={setFooterPadding}
              />
              
              <OverlaySettingsCard overlayMode={overlayMode} setOverlayMode={setOverlayMode} />
            </div>
          </TabsContent>
        </Tabs>

        {/* Translation History */}
        <TranslationHistoryCard translationHistory={translationHistory} />

        {/* Copyright */}
        <p className="mt-8 text-zinc-700 text-[10px] sm:text-xs text-center">
          {t("footerCopyright")}
        </p>
      </div>
    </div>
  );
}
